// Texture assets for the remaining 4 `pen='texture'` presets:
// 毛先 (bristle) / スプレー (spray) / 雲 (cloud) / カケアミ (hatch).
// (texture-brush-design.md §1 / §3 / §11 step 2.)
//
// PARITY (texture-brush-design.md §3, ABSOLUTE): like `paperTexture.ts`, each
// of these is a **raw alpha array** generated by a fixed deterministic formula
// (no `Math.random`). The web client (Canvas2D / OffscreenCanvas) and the
// server snapshot worker (`@napi-rs/canvas` / Skia) both `import` the exact
// byte array — there is no decoder in the loop to diverge. The §3 (a) route
// "bake real PNG → raw alpha" stays open for the final artwork without
// touching the `TextureSampler` seam (= these are placeholders with the right
// VISUAL CHARACTER per preset, not the finished art).
//
// Visual intent per preset (so each reads as distinct in the picker + canvas):
//   - bristle : directional筋 (parallel streaks along the texture x-axis;
//               stamp mapping rotates them to follow the stroke →毛先).
//   - spray   : coarse sparse 点群 (a stippled dot field →スプレー型紙).
//   - cloud   : fine multi-octave value noise, low contrast (柔らかいもや →雲).
//   - hatch   : diagonal斜線格子 (regular cross-hatch →カケアミ, world mapping
//               so the網点 stays aligned across overlapping strokes).
export const PRESET_TEXTURE_SIZE = 256;
/**
 * Deterministic integer hash → pseudo-random in `[0, 2^32)`. Pure
 * `Math.imul` / shift / xor (integer ops, bit-identical across V8 — no float,
 * no transcendental, no `Math.random`). Same construction as
 * `paperTexture.ts`'s `hash2` but with a salt so different presets get
 * independent grain from the same coords.
 */
const hash3 = (x, y, salt) => {
    let h = (Math.imul(x, 0x27d4_eb2d) ^ Math.imul(y, 0x1656_67b1) ^ Math.imul(salt, 0x9e37_79b1)) >>> 0;
    h = Math.imul(h ^ (h >>> 15), 0x85eb_ca6b) >>> 0;
    h = Math.imul(h ^ (h >>> 13), 0xc2b2_ae35) >>> 0;
    h = (h ^ (h >>> 16)) >>> 0;
    return h;
};
const clamp255 = (v) => (v < 0 ? 0 : v > 255 ? 255 : v);
/**
 * 毛先 (bristle): parallel streaks running along the texture x-axis. Each row
 * carries a constant base brightness, with a per-row祖密 (some rows are gaps =
 * low alpha, some are bristle筋 = high alpha) plus a light per-texel dither so
 * the筋 don't look like a perfect comb. Stamp mapping rotates the whole grid
 * to the stroke direction, so the streaks follow the brush.
 */
const buildBristleAlpha = (size) => {
    const out = new Uint8Array(size * size);
    for (let y = 0; y < size; y++) {
        // Row character: a bristle row (筋) vs a gap row. Use the hash on y so the
        // spacing is irregular (real bristles aren't evenly combed).
        const rowH = hash3(0, y, 11) % 100;
        const isBristle = rowH < 55; // ~55% of rows carry a streak
        const rowBase = isBristle ? 210 : 40;
        for (let x = 0; x < size; x++) {
            // Streaks run along x, so brightness varies slowly in x (long fibres)
            // with a small dither.
            const along = hash3(x >> 3, y, 12) % 40; // coarse variation along the筋
            const dither = (hash3(x, y, 13) % 31) - 15;
            out[y * size + x] = clamp255(rowBase + (isBristle ? -along : along) + dither);
        }
    }
    return out;
};
const SPRAY_TIERS = [
    { radius: 0, step: 2, hitPct: 45, salt: 41, base: 120, span: 90 }, // fine micro-specks
    { radius: 1, step: 5, hitPct: 38, salt: 42, base: 150, span: 90 }, // small grains
    { radius: 2.4, step: 11, hitPct: 30, salt: 43, base: 170, span: 80 }, // mid droplets
    { radius: 4.5, step: 28, hitPct: 22, salt: 44, base: 180, span: 75 }, // rare big blobs
];
/** Toroidal saturating-add of `v` into the 256-tiled buffer at (x, y). */
const sprayDeposit = (out, size, x, y, v) => {
    let tx = x % size;
    if (tx < 0)
        tx += size;
    let ty = y % size;
    if (ty < 0)
        ty += size;
    const i = ty * size + tx;
    const cur = out[i] ?? 0;
    out[i] = clamp255(cur + v);
};
const buildSprayAlpha = (size) => {
    const out = new Uint8Array(size * size);
    for (const tier of SPRAY_TIERS) {
        const slots = Math.ceil(size / tier.step);
        for (let sy = 0; sy < slots; sy++) {
            for (let sx = 0; sx < slots; sx++) {
                const h = hash3(sx, sy, tier.salt);
                if (h % 100 >= tier.hitPct)
                    continue; // empty slot → hole
                // Jitter the centre OFF the anchor grid by a per-slot hash (kills the
                // lattice regularity). Spread across the whole step so neighbouring
                // slots' specks can land close or far apart (irregular clustering).
                const jx = hash3(sx, sy, tier.salt + 100) % tier.step;
                const jy = hash3(sx, sy, tier.salt + 200) % tier.step;
                const cx = sx * tier.step + jx;
                const cy = sy * tier.step + jy;
                // Per-speck centre intensity (varies droplet to droplet).
                const peak = clamp255(tier.base + (hash3(sx, sy, tier.salt + 300) % tier.span));
                const r = tier.radius;
                if (r <= 0) {
                    sprayDeposit(out, size, cx, cy, peak); // single-texel micro-speck
                    continue;
                }
                // Round speck with a soft radial falloff: v = peak·(1 − (d/r)²).
                // Integer-only: compare d² to r² and scale by the integer ratio.
                const ri = Math.ceil(r);
                const r2 = r * r;
                for (let dy = -ri; dy <= ri; dy++) {
                    for (let dx = -ri; dx <= ri; dx++) {
                        const d2 = dx * dx + dy * dy;
                        if (d2 > r2)
                            continue;
                        // falloff in [0..1] → integer-scale peak. (d2/r2 uses float div but
                        // the result is identical across V8 platforms — IEEE-754 / + *.)
                        const fall = 1 - d2 / r2;
                        const v = Math.floor(peak * fall);
                        if (v > 0)
                            sprayDeposit(out, size, cx + dx, cy + dy, v);
                    }
                }
            }
        }
    }
    return out;
};
/**
 * Integer bilinear value-noise sample at one octave. The lattice spacing `cell`
 * MUST divide `size` so the corner-node wrap (`(g+1) % nodes`) makes the octave
 * tile seamlessly across the 256 world boundary. Returns 0..255.
 *
 * Deterministic: pure integer `hash3` + integer bilinear blend (no float
 * transcendental), so web (V8) and napi/Skia (V8) bake byte-identical bytes.
 */
const valueNoiseOctave = (x, y, size, cell, salt) => {
    const nodes = size / cell;
    const gx0 = Math.floor(x / cell);
    const gy0 = Math.floor(y / cell);
    const fx = x - gx0 * cell;
    const fy = y - gy0 * cell;
    const gx1 = (gx0 + 1) % nodes;
    const gy1 = (gy0 + 1) % nodes;
    const nodeVal = (gx, gy) => hash3(gx % nodes, gy % nodes, salt) % 256;
    const v00 = nodeVal(gx0, gy0);
    const v10 = nodeVal(gx1, gy0);
    const v01 = nodeVal(gx0, gy1);
    const v11 = nodeVal(gx1, gy1);
    const wx1 = fx;
    const wx0 = cell - fx;
    const wy1 = fy;
    const wy0 = cell - fy;
    const top = v00 * wx0 + v10 * wx1;
    const bot = v01 * wx0 + v11 * wx1;
    return Math.floor((top * wy0 + bot * wy1) / (cell * cell)); // 0..255
};
/**
 * 雲 (cloud): a FINE, SOFT fog/mist veil (細かく柔らかいもや).
 *
 * Re-tune (2026-06-19, after実機 "あからさまにまだら"): the previous version
 * used a single low-frequency lattice (32px nodes) pushed through a steep
 * contrast curve (`d*5/2` → 0..255). World-fixed, that read as big coarse
 * BLOCKS — obvious blotches with large holes — rather than fog. The previous
 * tuning over-corrected the earlier "looks like a faded brush (霧)" bug by
 * swinging all the way to high-contrast macro-mottle.
 *
 * New design = the middle ground between "まだら" (coarse blocks) and "ベタ"
 * (uniform brush): a **fine soft mist**.
 *   - HIGHER spatial frequency via MULTI-OCTAVE value noise. Three octaves at
 *     cell = 16 / 8 / 4 px (all exact divisors of 256) combined fBm-style with
 *     halving weights (4:2:1). The dominant 16px octave gives a soft drift, the
 *     8/4px octaves add fine detail that breaks up any big blob → no large
 *     lattice node visible. Result is detailed, not blocky.
 *   - LOWER contrast: the fBm sum is mapped to a NARROW mid band centred on a
 *     light-ish mean (~110) with a gentle gain (no near-0 holes, no near-255
 *     cores). A thin veil with subtle density variation, not punchy blobs.
 *   - STILL distinct from a brush: because it is world-mapped, the fine density
 *     variation is pinned to canvas coords and survives dense overpaint (a hole
 *     stays a hole), so a saturated fill keeps measurable alpha variance instead
 *     of collapsing to a flat wash. The variance is LOWER than the old punchy
 *     version (softer veil) but still clearly above a brush's near-zero variance.
 *
 * Distinct from: brush (solid, no variance) / spray (hard sparse dots) /
 * paper (high-frequency grain, higher contrast) / hatch (regular lines) /
 * bristle (directional streaks). cloud = low-contrast fine mist.
 */
const buildCloudAlpha = (size) => {
    const out = new Uint8Array(size * size);
    // fBm over 3 octaves with halving cell sizes (all divide 256 → seamless).
    // Weights 4:2:1 → total 7; the high-frequency octaves break up the dominant
    // one so no single lattice node reads as a blob.
    const CELLS = [16, 8, 4];
    const WEIGHTS = [4, 2, 1];
    const WSUM = 7;
    // Narrow output band: light mean, gentle gain. d = (fbm - 128); a small
    // multiplier keeps the swing tight (soft veil, no full holes/cores).
    // base = 110 (light), gain = 3/4 → max swing ≈ ±96 before clamp but the fBm
    // rarely reaches the extremes, so the effective band stays mid → reads soft.
    const BASE = 110;
    for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
            let acc = 0;
            for (let o = 0; o < CELLS.length; o++) {
                // Each octave gets its own salt so the lattices are uncorrelated.
                const cell = CELLS[o] ?? 1;
                const weight = WEIGHTS[o] ?? 0;
                acc += valueNoiseOctave(x, y, size, cell, 31 + o) * weight;
            }
            const fbm = acc / WSUM; // 0..255, dominated by the finest-weighted blend
            // Gentle contrast: pull toward BASE with a low gain so the veil is soft.
            const d = fbm - 128; // −128..127
            out[y * size + x] = clamp255(BASE + ((d * 3) >> 2)); // gain 3/4
        }
    }
    return out;
};
/**
 * カケアミ (hatch): a regular diagonal cross-hatch (斜線格子). Two families of
 * diagonal lines (`x+y` and `x-y` constant) drawn as thin high-alpha ridges
 * over a near-empty background. World mapped, so overlapping strokes keep the
 * 網点 aligned. Pure modular arithmetic → perfectly regular + deterministic.
 *
 * SEAMLESS TILE (bug fix 2026-06-19): the line spacing `period` MUST divide
 * the texture size (256) so the pattern is continuous across the world-mapping
 * wrap. The sampler reads texel `x=0` for world pixel `x=256`; the diagonal
 * phase `(x ± y) mod period` is continuous there only when `256 mod period == 0`
 * (then `(256 + y) mod period == (0 + y) mod period`). The old `period = 12`
 * does NOT divide 256 (256 = 21·12 + 4) → a 4-texel phase jump at the 256
 * boundary = visible seam where the斜線 fail to連続. `16` divides 256 evenly
 * (256 = 16·16), so both `x+y` and `x-y` diagonals wrap continuously.
 */
const HATCH_PERIOD = 16; // line spacing — MUST divide PRESET_TEXTURE_SIZE (256)
const buildHatchAlpha = (size) => {
    const out = new Uint8Array(size * size);
    const period = HATCH_PERIOD;
    const lineWidth = 2; // ridge thickness
    for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
            // Two diagonal families. `(x + y) mod period` near 0 → one diagonal,
            // `(x - y) mod period` near 0 → the crossing diagonal.
            let d1 = (x + y) % period;
            if (d1 < 0)
                d1 += period;
            let d2 = (x - y) % period;
            if (d2 < 0)
                d2 += period;
            const onLine = d1 < lineWidth || d2 < lineWidth;
            // Off-line texels MUST be exactly 0: under world mapping every overlapping
            // stroke reads the SAME texel for a given canvas pixel, and source-over
            // accumulation (`out = in + s·(1−in)`) builds up any non-zero gap alpha
            // until the 紙 地 goes solid. A real カケアミ darkens only the 線; the gaps
            // stay clean (the paper never fills). penAlpha·(0/255) = 0 contributes
            // nothing no matter how many times the gap is restamped → gaps stay 澄んだ.
            out[y * size + x] = onLine ? 235 : 0;
        }
    }
    return out;
};
// スクリーントーン 網点 was a `texture` preset (`tone-dot-light/mid/dark`,
// static dot alpha arrays). It has been promoted to its own `pen='tone'`
// category with a clustered-dot **threshold map** + a continuous 番手 (density),
// see `toneThreshold.ts` / texture-brush-design.md §12.2. The static dot
// generator + per-density alpha table were removed with that promotion (the
// threshold map reproduces every 番手 from one matrix).
const BRISTLE_ALPHA = buildBristleAlpha(PRESET_TEXTURE_SIZE);
const SPRAY_ALPHA = buildSprayAlpha(PRESET_TEXTURE_SIZE);
const CLOUD_ALPHA = buildCloudAlpha(PRESET_TEXTURE_SIZE);
const HATCH_ALPHA = buildHatchAlpha(PRESET_TEXTURE_SIZE);
/** 毛先 — stamp mapping (rotate-with-stroke), penWidth-following scale. */
export const BRISTLE_TEXTURE_SAMPLER = {
    mapping: 'stamp',
    width: PRESET_TEXTURE_SIZE,
    height: PRESET_TEXTURE_SIZE,
    alpha: BRISTLE_ALPHA,
    scale: 1,
};
/**
 * スプレー — world mapping (§1 要検証 → world 確定, 2026-06-19).
 *
 * Why world (not stamp): under stamp mapping each dab reads the stencil from
 * its OWN centre-relative coords, so densely overlapping dabs each pull a
 * DIFFERENT texel for the same canvas pixel — the holes of one dab get filled
 * by the bright specks of the next, and source-over accumulation collapses to
 * a solid wash ≈ a faded brush ("霧"). World mapping locks each texel to a
 * FIXED canvas coordinate, so a hole pixel is a hole for EVERY dab that covers
 * it and never fills in → the granular dot-spray survives dense strokes.
 *
 * Seamless world tile: specks are DEPOSITED into the 256-tiled buffer with
 * toroidal (mod 256) indexing (`sprayDeposit`), so a droplet whose disc crosses
 * the tile edge has its tail wrapped to the opposite edge. The across-seam
 * neighbour pair (texel 255 ↔ 256≡0) is therefore an ordinary in-tile pair →
 * the multi-scale spatter reads as one continuous field over 256+ px with no
 * phase jump (verified by the world-tile-seam test).
 */
export const SPRAY_TEXTURE_SAMPLER = {
    mapping: 'world',
    width: PRESET_TEXTURE_SIZE,
    height: PRESET_TEXTURE_SIZE,
    alpha: SPRAY_ALPHA,
    scale: 1,
};
/**
 * 雲 — world mapping (§1 → world 確定, 2026-06-19).
 *
 * Why world (not stamp): same root cause as spray. A stamp-mapped diffuse
 * texture lets each dab fill the previous dab's holes with its own dense cores,
 * so a dense stroke averages out to a flat mid-alpha ≈ a faded brush. World
 * mapping keeps every hole pinned to a canvas coordinate, so the もやっとした
 * low-frequency mottling (holes + cores) stays visible no matter how densely
 * the stroke is painted → reads as a 霧/雲, never as a brush.
 *
 * Seamless world tile: every fBm octave wraps its corner nodes via
 * `(g+1) % nodes` and each `cell` (16/8/4) is an exact divisor of 256, so the
 * blended value is continuous across the texel 255 → 0 wrap. Verified in the
 * tile-seam test.
 */
export const CLOUD_TEXTURE_SAMPLER = {
    mapping: 'world',
    width: PRESET_TEXTURE_SIZE,
    height: PRESET_TEXTURE_SIZE,
    alpha: CLOUD_ALPHA,
    scale: 1,
};
/** カケアミ — world mapping (網点 stays aligned across strokes). */
export const HATCH_TEXTURE_SAMPLER = {
    mapping: 'world',
    width: PRESET_TEXTURE_SIZE,
    height: PRESET_TEXTURE_SIZE,
    alpha: HATCH_ALPHA,
    scale: 1,
};
// (Screentone moved to `pen='tone'` + `toneThreshold.ts`; the static
// `TONE_DOT_*_TEXTURE_SAMPLER` exports were removed with the preset撤去.)
//# sourceMappingURL=presetTextures.js.map